import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { clear } = useCart();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [data, setData] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus(data.status === "expired" ? "expired" : "failed");
          return;
        }
        if (attempts.current++ < 8) setTimeout(poll, 2000);
        else setStatus("failed");
      } catch {
        if (attempts.current++ < 8) setTimeout(poll, 2000);
        else setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <SEO title="Order complete" description="Thanks for your purchase." noindex />
      <section className="container-narrow py-24 max-w-2xl">
        {status === "polling" && (
          <div className="text-center" data-testid="checkout-polling">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-brand-ink" />
            <p className="font-serif text-3xl mt-6 text-brand-ink">Confirming your payment…</p>
            <p className="text-brand-inkMuted mt-2">This will only take a moment.</p>
          </div>
        )}
        {status === "paid" && (
          <div className="bg-white border border-brand-line p-12 text-center" data-testid="checkout-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-brand-rust" />
            <h1 className="font-display text-4xl md:text-5xl mt-6 text-brand-ink">Thank you.</h1>
            <p className="text-brand-inkMuted mt-3">
              Your purchase is complete. Your items are available in <strong className="text-brand-ink">My Library</strong>.
            </p>
            {data?.items && (
              <ul className="mt-8 divide-y divide-brand-line border-y border-brand-line text-left">
                {data.items.map((i, idx) => (
                  <li key={`${idx}-${i.title}`} className="py-3 flex items-center justify-between">
                    <span className="text-brand-ink">{i.title}</span>
                    <span className="overline text-brand-inkMuted">{i.type}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-10 flex gap-4 justify-center">
              <button data-testid="go-to-library" onClick={() => navigate("/library")} className="bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors">Go to My Library</button>
              <Link to="/books" className="border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-7 py-3 text-sm font-medium tracking-wide transition-colors">Keep browsing</Link>
            </div>
          </div>
        )}
        {(status === "failed" || status === "expired") && (
          <div className="bg-white border border-brand-line p-12 text-center" data-testid="checkout-failed">
            <XCircle className="w-12 h-12 mx-auto text-brand-inkMuted" />
            <h1 className="font-display text-3xl mt-6 text-brand-ink">
              {status === "expired" ? "Payment session expired" : "We couldn't confirm your payment"}
            </h1>
            <p className="text-brand-inkMuted mt-3">No charges were made. You can try again from your cart.</p>
            <Link to="/cart" className="inline-block mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors">Return to cart</Link>
          </div>
        )}
      </section>
    </Layout>
  );
}
